import React, { useState } from "react";
import {
  Volume2,
  Music,
  Zap,
  Star,
  RotateCcw,
  Camera,
  HelpCircle,
  FileText,
  Shield,
  Home,
  User,
} from "lucide-react";
import FreeBetsModal from "./modals/FreeBetsModal";
import SessionBetHistoryModal from "./modals/SessionBetHistoryModal";
import GameLimitsModal from "./modals/GameLimitsModal";
import ProvablyFairModal from "./modals/provablyFairSettingsModal";
import HowToPlayModal from "./modals/HowToPlayModal";
import ChangeAvatarModal from "./modals/ChangeAvatarModal";
import { useAviator } from "../store/aviator";
import { useSocket } from "../providers/SocketProvider";

interface MenuItem {
  icon: React.ReactElement;
  label: string;
  hasToggle?: boolean;
  isEnabled?: boolean;
  onToggle?: () => void;
}

interface RegularMenuItem {
  icon: React.ReactElement;
  label: string;
  onClick?: () => void;
}

const DropdownComponent: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const { aviatorState, setAviatorState } = useAviator();
  const { username } = useSocket();

  const [showFreeBetsModal, setShowFreeBetsModal] = useState(false);
  const [showBetHistory, setShowBetHistory] = useState(false);
  const [showGameLimitsModal, setShowGameLimitsModal] = useState(false);
  const [showProvablyFairModal, setShowProvablyFairModal] = useState(false);
  const [showHowToPlayModal, setShowHowToPlayModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState(1);

  const getAvatarUrl = (id: number) => `/avatars/av-${id}.png`;

  const menuItems: MenuItem[] = [
    {
      icon: <Volume2 size={16} />,
      label: "Sound",
      hasToggle: true,
      isEnabled: aviatorState.fxChecked,
      onToggle: () =>
        setAviatorState((prev) => ({ ...prev, fxChecked: !prev.fxChecked })),
    },
    {
      icon: <Music size={16} />,
      label: "Music",
      hasToggle: true,
      isEnabled: aviatorState.musicChecked,
      onToggle: () =>
        setAviatorState((prev) => ({
          ...prev,
          musicChecked: !prev.musicChecked,
        })),
    },
    {
      icon: <Zap size={16} />,
      label: "Animation",
      hasToggle: true,
      isEnabled: aviatorState.animationChecked,
      onToggle: () =>
        setAviatorState((prev) => ({
          ...prev,
          animationChecked: !prev.animationChecked,
        })),
    },
  ];

  const regularMenuItems: RegularMenuItem[] = [
    {
      icon: <Star size={16} />,
      label: "Free Bets",
      onClick: () => setShowFreeBetsModal(true),
    },
    {
      icon: <RotateCcw size={16} />,
      label: "My Bet History",
      onClick: () => setShowBetHistory(true),
    },
    {
      icon: <Camera size={16} />,
      label: "Game Limits",
      onClick: () => setShowGameLimitsModal(true),
    },
    {
      icon: <HelpCircle size={16} />,
      label: "How To Play",
      onClick: () => setShowHowToPlayModal(true),
    },
    { icon: <FileText size={16} />, label: "Game Rules" },
    {
      icon: <Shield size={16} />,
      label: "Provably Fair Settings",
      onClick: () => setShowProvablyFairModal(true),
    },
  ];

  return (
    <div className="relative">
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full right-2 mt-0 w-80 bg-[#2c2d30] rounded-lg shadow-2xl z-50">
            {/* User section */}
            <div className="flex items-center justify-between p-[10px]">
              <div className="flex items-center space-x-3">
                <div className="relative w-10 h-10">
                  <img
                    src={getAvatarUrl(selectedAvatarId)}
                    alt="Current Avatar"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </div>
                <span
                  className="text-white font-bold text-base"
                  style={{ fontSize: "14px" }}
                >
                  {username}
                </span>
              </div>
              <button
                onClick={() => setShowAvatarModal(true)}
                className="flex items-center border border-[#414148] bg-[#252528] rounded-full px-2 py-2 text-[#83878e] transition-colors"
              >
                <div className="border border-[#414148] rounded-full p-1">
                  <User size={18} />
                </div>
                <span
                  className="ml-2 text-sm leading-[1rem] pr-2"
                  style={{ fontSize: "12px" }}
                >
                  Change
                  <br />
                  Avatar
                </span>
              </button>
            </div>

            {/* Toggle items */}
            <div>
              {menuItems.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between px-4 py-2.5 bg-[#1b1c1d] hover:bg-gray-750 transition-colors cursor-pointer ${
                    index !== menuItems.length - 1 ? "border-b" : ""
                  }`}
                  style={{ borderColor: "#2c2d30" }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-gray-400 w-4">{item.icon}</div>
                    <span
                      className="text-white font-normal text-base"
                      style={{ fontSize: "14px" }}
                    >
                      {item.label}
                    </span>
                  </div>

                  <button
                    onClick={item.onToggle}
                    className="w-[36px] h-[24px] p-[4px] rounded-full transition-colors relative bg-[#141516]"
                  >
                    <div
                      className={`w-[16px] h-[16px] rounded-full transition-transform ${
                        item.isEnabled
                          ? "translate-x-[0.75rem] bg-[#a3a3a3]"
                          : "bg-[#2c2d30]"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            {/* Spacer */}
            <div className="h-4" />

            {/* Regular menu items */}
            <div>
              {regularMenuItems.map((item, index) => (
                <div
                  key={index}
                  onClick={item.onClick}
                  className={`flex items-center px-4 py-2.5 bg-[#1b1c1d] hover:bg-gray-750 transition-colors cursor-pointer ${
                    index !== regularMenuItems.length - 1 ? "border-b" : ""
                  }`}
                  style={{ borderColor: "#2c2d30" }}
                >
                  <div className="text-gray-400 w-4 mr-3">{item.icon}</div>
                  <span
                    className="text-white font-normal text-base"
                    style={{ fontSize: "14px" }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Home section */}
            <div
              className="bg-gray-750 rounded-b-lg"
              onClick={() => (window.location.href = "/")}
            >
              <div className="flex items-center justify-center px-4 py-3 cursor-pointer bg-[#2c2d30] rounded-b-lg transition-colors">
                <Home size={16} className="text-gray-400 mr-2" />
                <span
                  className="text-gray-400 font-normal text-base"
                  style={{ fontSize: "14px" }}
                >
                  Home
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <FreeBetsModal
        isOpen={showFreeBetsModal}
        onClose={() => setShowFreeBetsModal(false)}
      />
      <SessionBetHistoryModal
        isOpen={showBetHistory}
        onClose={() => setShowBetHistory(false)}
      />
      <GameLimitsModal
        isOpen={showGameLimitsModal}
        onClose={() => setShowGameLimitsModal(false)}
      />
      <ProvablyFairModal
        isOpen={showProvablyFairModal}
        onClose={() => setShowProvablyFairModal(false)}
      />
      <HowToPlayModal
        isOpen={showHowToPlayModal}
        onClose={() => setShowHowToPlayModal(false)}
      />
      <ChangeAvatarModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        selectedId={selectedAvatarId}
        onSelect={(id) => setSelectedAvatarId(id)}
      />
    </div>
  );
};

export default DropdownComponent;
